import { AI_ASSISTANT } from "@/config/constants";
import {
  SLA_AUDIENCE,
  SLA_CLOCK,
  SLA_DURATION_UNIT,
  type SlaAudience,
  type SlaClock,
  type SlaDurationUnit,
} from "@/types/workspace";

export const SLA_CLOCK_ORDER: SlaClock[] = [
  SLA_CLOCK.FIRST_REPLY,
  SLA_CLOCK.SUBSEQUENT_REPLY,
  SLA_CLOCK.OPEN_CONVERSATION,
  SLA_CLOCK.FOLLOW_UP_OVERDUE,
  SLA_CLOCK.AI_WAITING,
  SLA_CLOCK.UNASSIGNED,
];

export const SLA_CLOCK_COPY: Record<
  SlaClock,
  {
    label: string;
    /** How a miss of this clock reads in reports. */
    missed: string;
    description: string;
    example: string;
    offTarget: string;
  }
> = {
  [SLA_CLOCK.FIRST_REPLY]: {
    label: "First reply",
    missed: "First reply late",
    description:
      "How long a customer waits for their first answer. Starts when the conversation lands with a teammate and stops when they send the first reply.",
    example:
      "Example: with a 1 hour target, a message that arrives at 9:00 needs an answer by 10:00.",
    offTarget: "how quickly a first reply should go out",
  },
  [SLA_CLOCK.SUBSEQUENT_REPLY]: {
    label: "Subsequent reply",
    missed: "Reply late",
    description:
      "How long a customer waits for every answer after the first. Starts again each time they write back and stops when anyone replies, AI included.",
    example:
      "Example: they write again at 11:00, so the clock runs from 11:00 until your next reply goes out.",
    offTarget: "how quickly later replies should go out",
  },
  [SLA_CLOCK.OPEN_CONVERSATION]: {
    label: "Open conversation",
    missed: "Open too long",
    description:
      "How long a conversation stays open before someone closes it. Time keeps counting while it is snoozed.",
    example:
      "Example: with a 2 day target, a chat opened on Monday morning should be closed by Wednesday morning.",
    offTarget: "how long a conversation may stay open",
  },
  [SLA_CLOCK.FOLLOW_UP_OVERDUE]: {
    label: "Follow-up overdue",
    missed: "Follow-up overdue",
    description:
      "How late a follow-up runs. Starts the moment a follow-up passes its due time and stops when someone marks it done.",
    example:
      "Example: a follow-up due Friday at 15:00 and still open at 16:00 is one hour late.",
    offTarget: "how late a follow-up may run",
  },
  [SLA_CLOCK.AI_WAITING]: {
    label: `${AI_ASSISTANT.name} waiting`,
    missed: `${AI_ASSISTANT.name} left waiting`,
    description: `How long ${AI_ASSISTANT.name} waits on a person: a handover, a draft, an action to approve or a question. Starts when ${AI_ASSISTANT.name} asks and stops when someone picks it up.`,
    example:
      "Example: with a 15 minute target, a question asked at 14:00 needs an answer by 14:15.",
    offTarget: `how long ${AI_ASSISTANT.name} may wait on a person`,
  },
  [SLA_CLOCK.UNASSIGNED]: {
    label: "Unassigned",
    missed: "No owner in time",
    description:
      "How long a conversation sits with nobody looking after it. Starts when it arrives or goes back to the pool and stops when someone is assigned. There is no owner yet to warn, so pick teammates to notify.",
    example:
      "Example: with a 10 minute target, a chat that arrives at 9:00 needs an owner by 9:10.",
    offTarget: "how long a conversation may sit with no owner",
  },
};

export const DURATION_UNIT_ORDER: SlaDurationUnit[] = [
  SLA_DURATION_UNIT.MINUTES,
  SLA_DURATION_UNIT.HOURS,
  SLA_DURATION_UNIT.DAYS,
];

export const DURATION_UNIT_MINUTES: Record<SlaDurationUnit, number> = {
  minutes: 1,
  hours: 60,
  days: 1440,
};

export const DURATION_UNIT_LABEL: Record<
  SlaDurationUnit,
  { one: string; other: string }
> = {
  minutes: { one: "minute", other: "minutes" },
  hours: { one: "hour", other: "hours" },
  days: { one: "day", other: "days" },
};

/** Headings for the two blocks a live clock opens up, each labelling its switch. */
export const SLA_REMINDER_LABEL = "Reminder";
export const SLA_BREACH_LABEL = "Missed target";

export const SLA_TARGET_LABEL = "Target";
export const SLA_REMINDER_PERCENT_LABEL = "Remind at";

/** The row caption already says these are who gets notified, so the field only
 *  has to introduce the who. */
export const SLA_AUDIENCE_LABEL_TEXT = "Notify";

/** Off is a real state, not an empty one: the row has to say what stops being
 *  measured, not just how to leave it. */
export const SLA_CLOCK_OFF_TITLE =
  "This clock is off. Nothing is timed here and nobody is told.";

export const slaClockOffHint = (offTarget: string) =>
  `Turn it on to set ${offTarget}, then choose who gets a heads-up while there is still time and who is told once the time runs out.`;

export const SLA_REMINDER_DESCRIPTION =
  "A heads-up while there is still time to act.";
export const SLA_BREACH_DESCRIPTION =
  "The time ran out and it still has not happened.";

/** A silenced breach is not an unmeasured one, and the row has to say so or
 *  switching it off reads like switching the clock off. */
export const SLA_BREACH_SILENT_HINT =
  "Misses are still counted in your reports, but nobody is told when one happens.";

export const SLA_REMINDER_SILENT_HINT =
  "No heads-up: the first anyone hears is when the time has already run out.";

/** Two roles sit in the recipient picker beside real people. The wire model's
 *  audience enum is derived from which of them is picked - see
 *  `fromRecipientKeys` - so the assignee is a chip you can see rather than an
 *  implication of the word "Both". */
export const SLA_RECIPIENT_ASSIGNEE = "__assignee__";
export const SLA_RECIPIENT_EVERYONE = "__everyone__";

export const SLA_RECIPIENT_ROLE_COPY: Record<
  string,
  { label: string; icon: string }
> = {
  [SLA_RECIPIENT_ASSIGNEE]: {
    label: "Assignee",
    icon: "solar:user-check-rounded-linear",
  },
  [SLA_RECIPIENT_EVERYONE]: {
    label: "Everyone in workspace",
    icon: "solar:users-group-rounded-linear",
  },
};

export const DEFAULT_SLA_REMINDER_PERCENT = 80;
export const DEFAULT_SLA_DURATION_VALUE = 1;
export const DEFAULT_SLA_DURATION_UNIT: SlaDurationUnit =
  SLA_DURATION_UNIT.HOURS;
export const SLA_MAX_DURATION_MINUTES = 43200;

export const SLA_AUDIENCE_ORDER: SlaAudience[] = [
  SLA_AUDIENCE.WORKSPACE_USERS,
  SLA_AUDIENCE.ASSIGNEE,
  SLA_AUDIENCE.BOTH,
];

/** The assignee, not the whole workspace. Blasting every member is a real
 *  option - the `unassigned` clock has no one else to tell - but as a default it
 *  is just alarm fatigue, and `audienceFor` still clamps that one clock. */
export const DEFAULT_SLA_BREACH_AUDIENCE: SlaAudience = SLA_AUDIENCE.ASSIGNEE;
export const DEFAULT_SLA_REMINDER_AUDIENCE: SlaAudience = SLA_AUDIENCE.ASSIGNEE;

/** A reminder is the extra nudge, so it is opt-in; a breach is the alert itself,
 *  so it starts on. The asymmetry is deliberate - see the schema's columns. */
export const DEFAULT_SLA_NOTIFY_ON_REMINDER = false;
export const DEFAULT_SLA_NOTIFY_ON_BREACH = true;

export const SLA_SETTINGS_PATH = "/settings/sla";
